/**
 * useHydrateRoyaleFromResults
 *
 * Bridges a COMPLETED tournament's REST payload (`detail.results`) into the
 * live royale zustand store, so the ordinary `PuzzleRoyaleBattle` ended screen
 * can render it verbatim — no parallel "review" layout to keep in sync.
 *
 * WHY THIS EXISTS
 * ---------------
 * `PuzzleRoyaleBattle` derives its whole ended state from the store (`status`,
 * `session`, `players`, `leaderboard`, `trails`, `endResult`). That store is a
 * plain `create()` with NO persist middleware — it is pure in-memory socket
 * state. It's populated because you were on the page while the race ran.
 *
 * A completed tournament is opened COLD from a URL (`/puzzle-royale-tournament/16`),
 * often days later and sometimes by someone who never played. At that moment
 * `status` is 'idle', `session` is null, `trails` is {} and `leaderboard` is []
 * — every input the ended screen reads is empty, so it renders a blank lobby.
 *
 * The data does exist, just in a different shape: `results.players[].attempts`
 * (`[{puzzleId, solved}]`) rather than `trails` (`"<id>:<uci>:<0|1>"` strings),
 * and `points`/`score`/`rank` columns rather than a live leaderboard array.
 * This hook performs that translation and calls the store's OWN
 * `hydrateRoyale` + `endRoyale` actions, so the derived state (ranking,
 * pieces, combos) is computed by the same code the live path uses.
 *
 * SAFETY
 * ------
 * - Refuses to run when a REAL session is in flight (`status` is 'in_battle' or
 *   'lobby'), so opening a finished tournament in a second tab can never
 *   clobber a race you're playing.
 * - Resets the store on unmount, so this synthetic state never leaks into a
 *   subsequent live game.
 * - `uci` is NOT persisted per attempt, so the trail's move component is a
 *   placeholder. Only the trailing `:1`/`:0` flag drives the grid's green/red,
 *   which is exactly what the review needs. The spectate "reconstruct last
 *   move" path is not used post-game.
 */

import {useEffect, useMemo} from 'react';

import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
// BattleType is a runtime enum and lives in ./types — royaleTypes only re-imports
// it for its own annotations and does NOT re-export it.
import {BattleType} from './types';
import type {RoyaleSession, RoyaleLobbyPlayer} from './royaleTypes';
import type {
  RoyaleTournamentDetail,
  RoyaleTournamentPlayerResult,
} from '@/Hooks/FunChallengesApi/useRoyaleTournaments';
import {tournamentDisplayName} from './util/tournamentLink';

/** One persisted attempt row as stored in `player.attempts`. */
interface StoredAttempt {
  puzzleId?: number;
  solved?: boolean;
}

/**
 * Hydrate the royale store from a completed tournament's results.
 *
 * @returns `true` once the store has been seeded (so the caller can render the
 *          battle screen), `false` while it can't / shouldn't hydrate.
 */
export default function useHydrateRoyaleFromResults(
  detail: RoyaleTournamentDetail | null | undefined,
  myUserId: string,
): boolean {
  const status = useFunChallengesRoyaleStore(s => s.status);
  const hydrateRoyale = useFunChallengesRoyaleStore(s => s.hydrateRoyale);
  const endRoyale = useFunChallengesRoyaleStore(s => s.endRoyale);
  const reset = useFunChallengesRoyaleStore(s => s.reset);

  const results = detail?.results;

  // NEVER hydrate over a live race (a second tab playing a different royale).
  const liveSessionInFlight = status === 'in_battle' || status === 'lobby';

  const payload = useMemo(() => {
    if (!results) return null;
    const players = (results.players as RoyaleTournamentPlayerResult[]) || [];
    if (!players.length) return null;

    const rawIds = (results.puzzle_royale_detail as any)?.puzzle_ids;
    const coords: string[] = Array.isArray(rawIds)
      ? rawIds.map((n: unknown) => String(n))
      : [];

    // Names come from the ROSTER, not the results rows: the results table
    // (`cc_puzzle_royale_player_results`) stores no username, and
    // `_serialize_pr_player` has no `user_name` field — which is why every row
    // rendered as "Player <userid>". The same detail payload carries `roster`
    // with the names, so join on user_id here.
    const nameByUserId: Record<string, string> = {};
    (detail?.roster || []).forEach(r => {
      const n = (r.user_name || '').trim();
      if (n) nameByUserId[String(r.user_id)] = n;
    });

    const lobbyPlayers: RoyaleLobbyPlayer[] = [];
    const allResponses: Record<string, string[]> = {};
    const scores: Record<
      string,
      {points: number; solved: number; wrong: number; bestCombo: number}
    > = {};

    players.forEach(p => {
      const uid = String(p.user_id);
      lobbyPlayers.push({
        userId: uid,
        name: nameByUserId[uid] || p.user_name || `Player ${uid}`,
        avatarUrl: '',
        isHost: String(detail?.created_by ?? '') === uid,
      });

      // attempts → trail triples. The store only reads the trailing solved
      // flag for the grid's green/red, so `uci` is a placeholder ('-').
      const attempts: StoredAttempt[] = Array.isArray(p.attempts)
        ? (p.attempts as StoredAttempt[])
        : [];
      allResponses[uid] = attempts.map(
        a => `${a?.puzzleId ?? 0}:-:${a?.solved ? 1 : 0}`,
      );

      scores[uid] = {
        points: p.points ?? 0,
        solved: p.score ?? 0,
        wrong: p.wrong ?? 0,
        bestCombo: p.best_combo ?? 0,
      };
    });

    // Final ranking, winner first — the server already stamped `rank`.
    const rankedPlayers = [...players]
      .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
      .map(p => String(p.user_id));

    const session: RoyaleSession = {
      sessionId: String(detail?.session_id || ''),
      roomId: String(detail?.room_id || ''),
      myUserId: String(myUserId),
      battleType: BattleType.PUZZLE_ROYALE,
      // The race is over: both clocks are in the past so nothing counts down
      // and the clock pill reads 0:00.
      startsAt: 1,
      endsAt: 1,
      settings: {
        timerSec: detail?.timer_sec ?? 90,
        difficulty: (detail?.difficulty as any) ?? 'easy',
        topics: (detail?.topics as any) ?? [],
        maxPlayers: detail?.max_players ?? 100,
        // Identity of the thing being reviewed. Post-race the battle screen's
        // Details tab is the ONLY Details surface (the scheduled-lobby one is
        // gone), and it reads `session.settings` — so without these the ended
        // screen showed settings with no indication of WHICH tournament.
        // `isTournament` gates the tournament-only rows.
        isTournament: true,
        tournamentName: tournamentDisplayName({
          id: detail?.id ?? '',
          name: detail?.name,
        }),
        scheduledAt: detail?.scheduled_at || '',
      } as any,
      coords,
    };

    return {session, lobbyPlayers, allResponses, scores, rankedPlayers};
  }, [results, detail, myUserId]);

  const canHydrate = !!payload && !liveSessionInFlight;

  useEffect(() => {
    if (!canHydrate || !payload) return;
    // `endedSnapshot: true` — land straight in 'ended'. Without it the store sat
    // in 'in_battle' for one render, which the global listener read as a live
    // race starting and navigated to the standalone battle route (the "wrong tab,
    // then switches back" flash when opening a finished tournament).
    hydrateRoyale(
      payload.session,
      payload.lobbyPlayers,
      payload.allResponses,
      payload.scores,
      true,
    );
    // Flip to the ENDED state — this is what makes the battle screen render its
    // post-game view (grid reviewable, board locked, standings final).
    endRoyale({
      sessionId: payload.session.sessionId,
      battleType: BattleType.PUZZLE_ROYALE,
      reason: 'time_up',
      rankedPlayers: payload.rankedPlayers,
      scores: payload.scores,
      responses: payload.allResponses,
    });
  }, [canHydrate, payload, hydrateRoyale, endRoyale]);

  // Clear the synthetic state on unmount so it can't leak into a later live
  // game. Guarded so we never wipe a real session we didn't create.
  //
  // Gate on `isReviewSnapshot`, NOT `status === 'ended'`: a REAL race also ends
  // in 'ended', so the old check would wipe a genuine just-finished session if
  // this screen happened to unmount then (e.g. a live start arrived while the
  // user was reviewing an old tournament, and their real race finished before
  // they navigated away). The flag identifies state THIS hook created, which is
  // exactly what "never wipe a session we didn't create" means. It is also
  // cleared by `reset()` in routeRoyaleSessionStart, so once a live session has
  // taken over the store this cleanup correctly becomes a no-op.
  useEffect(() => {
    return () => {
      const s = useFunChallengesRoyaleStore.getState();
      if (s.isReviewSnapshot) reset();
    };
  }, [reset]);

  return canHydrate;
}
